import React, { useEffect, useMemo, useState } from 'react'

interface PrizeItem {
  prize_title: string
  prize_date: string
}

export interface TurboCountdownTimerData {
  title?: string
  prizes: PrizeItem[]
}

const timerBGStyles: React.CSSProperties = {
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: "'tnum'",
  minWidth: '2ch',
  display: 'inline-block',
  textAlign: 'center'
}

const timerDayStyle: React.CSSProperties = {
  background: `linear-gradient(180deg, #FDFEFD 27.14%, #F7B205 42.86%, #FEEE1D 52.45%, #C67C13 93.86%)`,
  ...timerBGStyles
}

const timerHourStyle: React.CSSProperties = {
  background: `linear-gradient(180deg, #FDFEFD 20.24%, #00CC43 42.86%, #26FF6D 53.98%, #15B94B 93.86%)`,
  ...timerBGStyles
}

const timerMinuteStyle: React.CSSProperties = {
  background: `linear-gradient(180deg, #FDFEFD 20.24%, #AD3E8F 42.86%, #F83EBA 53.98%, #7F185D 93.86%)`,
  ...timerBGStyles
}

const timerSecondStyle: React.CSSProperties = {
  background: `linear-gradient(180deg, #FDFEFD 25.22%, #70B9DD 42.86%, #9CD2E4 53.98%, #52BADD 93.86%)`,
  ...timerBGStyles
}

const MOUNTAIN_TIME_ZONE = 'America/Denver'
const MOUNTAIN_FALLBACK_OFFSET = -7 * 60 // minutes
const DEFAULT_WINDOW_MS = 24 * 60 * 60 * 1000

interface PrizeWithTimestamp extends PrizeItem {
  timestamp: number
}

interface TimeParts {
  days: number
  hours: number
  minutes: number
  seconds: number
}

const getFormattedPartsForZone = (date: Date, timeZone: string): Intl.DateTimeFormatPart[] => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'shortOffset'
  })

  return formatter.formatToParts(date)
}

const getOffsetMinutes = (parts: Intl.DateTimeFormatPart[]): number => {
  const timeZonePart = parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
  const match = timeZonePart.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/)

  if (!match) {
    return MOUNTAIN_FALLBACK_OFFSET
  }

  const sign = match[1].startsWith('-') ? -1 : 1
  const hours = Math.abs(parseInt(match[1], 10))
  const minutes = match[2] ? parseInt(match[2], 10) : 0

  return sign * (hours * 60 + minutes)
}

const getCurrentTimeInZone = (timeZone: string): number => {
  const now = new Date()
  const parts = getFormattedPartsForZone(now, timeZone)
  const year = parseInt(parts.find((part) => part.type === 'year')?.value ?? '0', 10)
  const month = parseInt(parts.find((part) => part.type === 'month')?.value ?? '1', 10) - 1
  const day = parseInt(parts.find((part) => part.type === 'day')?.value ?? '1', 10)
  const hour = parseInt(parts.find((part) => part.type === 'hour')?.value ?? '0', 10)
  const minute = parseInt(parts.find((part) => part.type === 'minute')?.value ?? '0', 10)
  const second = parseInt(parts.find((part) => part.type === 'second')?.value ?? '0', 10)
  const offsetMinutes = getOffsetMinutes(parts)

  const utcMilliseconds = Date.UTC(year, month, day, hour, minute, second)

  return utcMilliseconds - offsetMinutes * 60 * 1000
}

const normaliseDateString = (date?: string): number | null => {
  if (!date) {
    return null
  }

  const trimmedDate = date.trim()
  if (!trimmedDate) {
    return null
  }

  const hasTime = trimmedDate.includes('T')
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/.test(trimmedDate)
  let normalisedDateString = trimmedDate

  if (!hasTime) {
    normalisedDateString = `${normalisedDateString}T00:00:00`
  }

  if (!hasOffset) {
    normalisedDateString = `${normalisedDateString}-07:00`
  }

  const parsedDate = new Date(normalisedDateString)

  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return parsedDate.getTime()
}

const findActivePrizeIndex = (now: number, prizeList: PrizeWithTimestamp[]): number => {
  if (!prizeList.length) return -1

  const upcomingIndex = prizeList.findIndex((prize) => prize.timestamp >= now)

  if (upcomingIndex >= 0) {
    return upcomingIndex
  }

  return prizeList.length - 1
}

const calculateTimeRemaining = (targetTimestamp: number | undefined, now: number): TimeParts => {
  if (!targetTimestamp) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 }
  }

  const diff = Math.max(targetTimestamp - now, 0)
  const totalSeconds = Math.floor(diff / 1000)

  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return { days, hours, minutes, seconds }
}

const formatTimeUnit = (value: number): string => value.toString().padStart(2, '0')

const TurboCountdownTimer: React.FC<TurboCountdownTimerData> = ({ title, prizes }) => {
  const prizeList = useMemo<PrizeWithTimestamp[]>(() => {
    if (!Array.isArray(prizes)) return []

    return prizes
      .map((prize) => {
        const timestamp = normaliseDateString(prize.prize_date)
        if (timestamp === null) return null

        return {
          ...prize,
          timestamp
        }
      })
      .filter((prize): prize is PrizeWithTimestamp => prize !== null)
      .sort((a, b) => a.timestamp - b.timestamp)
  }, [prizes])

  const [activePrizeIndex, setActivePrizeIndex] = useState<number>(-1)
  const [timeRemaining, setTimeRemaining] = useState<TimeParts>({ days: 0, hours: 0, minutes: 0, seconds: 0 })

  useEffect(() => {
    if (!prizeList.length) {
      setActivePrizeIndex(-1)
      setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 })
      return
    }

    const now = getCurrentTimeInZone(MOUNTAIN_TIME_ZONE)
    const index = findActivePrizeIndex(now, prizeList)
    const activePrize = index >= 0 ? prizeList[index] : undefined

    setActivePrizeIndex(index)
    setTimeRemaining(calculateTimeRemaining(activePrize?.timestamp, now))
  }, [prizeList])

  useEffect(() => {
    if (!prizeList.length) {
      return
    }

    const intervalId = window.setInterval(() => {
      const now = getCurrentTimeInZone(MOUNTAIN_TIME_ZONE)
      const nextIndex = findActivePrizeIndex(now, prizeList)
      const nextPrize = nextIndex >= 0 ? prizeList[nextIndex] : undefined

      if (nextIndex !== activePrizeIndex) {
        setActivePrizeIndex(nextIndex)
      }

      setTimeRemaining(calculateTimeRemaining(nextPrize?.timestamp, now))
    }, 1000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [activePrizeIndex, prizeList])

  const headingPrefix = title?.trim() ?? ''
  const activePrizeTitle = activePrizeIndex >= 0 ? prizeList[activePrizeIndex]?.prize_title?.trim() ?? '' : ''
  const shouldRenderHeading = Boolean(headingPrefix || activePrizeTitle)
  const remainingMilliseconds = (((timeRemaining.days * 24 + timeRemaining.hours) * 60 + timeRemaining.minutes) * 60 + timeRemaining.seconds) * 1000
  const activePrize = activePrizeIndex >= 0 ? prizeList[activePrizeIndex] : undefined
  const currentTimestamp = activePrize ? activePrize.timestamp - remainingMilliseconds : undefined
  const previousPrizeTimestamp = activePrizeIndex > 0 ? prizeList[activePrizeIndex - 1]?.timestamp : undefined

  let windowStartTimestamp: number | undefined

  if (activePrize) {
    const fallbackStart = activePrize.timestamp - DEFAULT_WINDOW_MS
    if (typeof previousPrizeTimestamp === 'number') {
      const gap = activePrize.timestamp - previousPrizeTimestamp
      if (gap > 0 && gap <= DEFAULT_WINDOW_MS) {
        windowStartTimestamp = previousPrizeTimestamp
      } else {
        windowStartTimestamp = fallbackStart
      }
    } else {
      windowStartTimestamp = fallbackStart
    }
  }

  const windowDurationMs = activePrize && typeof windowStartTimestamp === 'number' ? Math.max(activePrize.timestamp - windowStartTimestamp, 0) : 0

  const elapsedMilliseconds =
    windowDurationMs > 0 && typeof currentTimestamp === 'number' && typeof windowStartTimestamp === 'number'
      ? Math.max(currentTimestamp - windowStartTimestamp, 0) : 0

  const progressRatio = windowDurationMs > 0 ? Math.min(elapsedMilliseconds / windowDurationMs, 1) : 0
  const progressWidth = `${(progressRatio * 100).toFixed(2)}%`

  return (
    <div className="w-full">
      {shouldRenderHeading ? 
        <h3 className="font-jetBrainsMono text-[14px] lg:text-base uppercase text-white mb-2 lg:mb-0">
          {headingPrefix}
          {headingPrefix ? ' ' : null}
          <span>{activePrizeTitle}</span>
        </h3>
        : null
      }

      <div className="flex items-center justify-between p-2.5">
        <div className="flex items-center flex-col">
          <h4 
            className="font-orbitron text-[36px] lg:text-[72px] leading-[40px] lg:leading-[72px] tracking-[-1.44px] lg:tracking-[-2.88px] turbo-countdown-numbers" 
            style={timerDayStyle}>
            {formatTimeUnit(timeRemaining.days)}
          </h4>
          <p className="text-[12px] lg:text-[21px] font-bold uppercase text-white opacity-30">DAYS</p>
        </div>

        <div className="flex items-center flex-col">
          <h4 
            className="font-orbitron text-[36px] lg:text-[72px] leading-[40px] lg:leading-[72px] tracking-[-1.44px] lg:tracking-[-2.88px] turbo-countdown-numbers" 
            style={timerHourStyle}>
            {formatTimeUnit(timeRemaining.hours)}
          </h4>
          <p className="text-[12px] lg:text-[21px] font-bold uppercase text-white opacity-30">HOURS</p>
        </div>

        <div className="flex items-center flex-col">
          <h4 
            className="font-orbitron text-[36px] lg:text-[72px] leading-[40px] lg:leading-[72px] tracking-[-1.44px] lg:tracking-[-2.88px] turbo-countdown-numbers" 
            style={timerMinuteStyle}>
            {formatTimeUnit(timeRemaining.minutes)}
          </h4>
          <p className="text-[12px] lg:text-[21px] font-bold uppercase text-white opacity-30">MIN</p>
        </div>

        <div className="flex items-center flex-col">
          <h4 
            className="font-orbitron text-[36px] lg:text-[72px] leading-[40px] lg:leading-[72px] tracking-[-1.44px] lg:tracking-[-2.88px] turbo-countdown-numbers" 
            style={timerSecondStyle}>
            {formatTimeUnit(timeRemaining.seconds)}
          </h4>
          <p className="text-[12px] lg:text-[21px] font-bold uppercase text-white opacity-30">SEC</p>
        </div>
      </div>

      <div className="relative h-4 w-full mt-4 lg:mt-2 bg-[linear-gradient(180deg,#CBCBCB_0%,#EDEDED_18.5%,#EDEDED_24.5%,#E0E0E0_42%,#F9F9F9_79.5%)] shadow-[0_-1px_0_0_rgba(0,0,0,0.15),inset_0_1px_0_0_rgba(0,0,0,0.25)_inset]">
        <div
          className="absolute left-0 top-0 h-full bg-[linear-gradient(180deg,#0076cc,#3bdcff)]"
          style={{ width: progressWidth, transition: 'width 0.5s linear' }}
        >
          <div className="absolute left-0 top-0 h-[9px] w-full bg-[linear-gradient(180deg,#fff,#fff0)]" />
        </div>
      </div>
    </div>
  )
}

export default TurboCountdownTimer
